import clsx from 'clsx';

import {
  body,
  h2,
  h3,
  hand,
} from '@frontend/storefront/components/home/styles';
import Image from '@frontend/storefront/Image';

type Quote = { text: string; author: string };

export function Testimonials({ quotes }: { quotes: Quote[] }) {
  return (
    <section className="np-section pt-4">
      <div className="np-container grid grid-cols-[repeat(auto-fit,minmax(260px,377px))] gap-x-10 gap-y-8 desk:gap-x-[143px] desk:ps-[81px]">
        {quotes.map((quote) => (
          <blockquote key={quote.author} className={clsx(hand, 'text-[19px]')}>
            <p>&ldquo;{quote.text}&rdquo;</p>
            <p>&mdash; {quote.author}</p>
          </blockquote>
        ))}
      </div>
    </section>
  );
}

export function Features({
  heading,
  features,
}: {
  heading: string;
  features: { icon: string; title: string; text: string }[];
}) {
  return (
    <section className="my-[clamp(24px,3.5vw,48px)] bg-np-orange pt-[45px] pb-[67px]">
      <div className="np-container px-[33px]">
        <h2 className={h2}>{heading}</h2>
        <div className="mt-10 grid max-w-[1113px] grid-cols-1 gap-12 desk:mt-[88px] desk:grid-cols-[repeat(3,minmax(0,320px))] desk:justify-between desk:gap-10">
          {features.map((feature) => (
            <div key={feature.title}>
              <div className="mb-5 flex h-[110px] items-start desk:mb-10 desk:h-[142px]">
                <Image
                  src={feature.icon}
                  alt=""
                  width={120}
                  height={130}
                  className="h-auto max-h-full w-auto"
                />
              </div>
              <h3 className={clsx(h3, 'mb-3')}>{feature.title}</h3>
              <p className={body}>{feature.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function BookGrid({
  heading,
  books,
}: {
  heading: string;
  books: { title: string; src: string }[];
}) {
  return (
    <section className="np-section">
      <div className="np-container">
        <h2 className={h2}>{heading}</h2>
        <div className="mt-6 grid grid-cols-2 gap-2 desk:mt-10 desk:grid-cols-4">
          {books.map((book) => (
            <div
              key={book.title}
              className="flex aspect-[340/448] items-center justify-center overflow-hidden rounded-np-md bg-np-card"
            >
              <Image
                src={book.src}
                alt={book.title}
                width={900}
                height={900}
                sizes="(min-width: 990px) 25vw, 50vw"
                className="aspect-square w-[112%] max-w-none object-contain"
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function Faq({
  heading,
  questions,
}: {
  heading: string;
  questions: { question: string; answer: string }[];
}) {
  return (
    <section className="np-section">
      <div className="np-container grid grid-cols-1 items-start gap-2 desk:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] desk:gap-12">
        <h2 className={h2}>{heading}</h2>
        <div>
          {questions.map((item) => (
            <details
              key={item.question}
              className="group border-b border-np-ink/20 first:border-t"
            >
              <summary className="flex cursor-pointer list-none items-center justify-between gap-6 py-6 [&::-webkit-details-marker]:hidden">
                <span className={h3}>{item.question}</span>
                <span aria-hidden="true" className="relative size-4 flex-none">
                  <span className="absolute inset-x-0 top-[7px] h-0.5 rounded-[1px] bg-current" />
                  <span className="absolute inset-x-0 top-[7px] h-0.5 rotate-90 rounded-[1px] bg-current transition-transform duration-200 group-open:rotate-0" />
                </span>
              </summary>
              <p className={clsx(body, 'max-w-[640px] pb-6')}>{item.answer}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}
